import { useEffect, useState } from 'react'
import { Link2, Radio } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { AgentLiveStreamPanel } from '@/components/features/agent/agent-live-stream-panel'
import {
  fetchCloudAgentStatus,
  isActiveCloudDispatch,
} from '@/lib/cloud-dispatch'
import { testIds } from '@/lib/test-ids'

export type AgentDispatchMeta = {
  agentId?: string
  dispatch_status?: string
  dispatch_organization_id?: string
  stream_url?: string
  pr_url?: string
  branch?: string
  handoffTitle?: string
}

type DispatchStatus =
  | 'running'
  | 'complete'
  | 'failed'
  | 'timeout'
  | 'cancelled'
  | 'error'
  | 'unknown'
  | 'checking'

function normalizeStatus(raw: string | undefined): DispatchStatus {
  if (!raw || raw === 'checking') return 'checking'
  if (
    raw === 'running' ||
    raw === 'complete' ||
    raw === 'failed' ||
    raw === 'timeout' ||
    raw === 'cancelled' ||
    raw === 'error' ||
    raw === 'unknown'
  ) {
    return raw
  }
  return 'unknown'
}

function StatusBadge({ status, agentId }: { status: DispatchStatus; agentId?: string }) {
  if (status === 'checking') {
    return (
      <span className="text-xs text-muted-foreground font-medium">Checking status...</span>
    )
  }
  if (status === 'running') {
    return (
      <>
        <span className="inline-block h-2 w-2 rounded-full bg-blue-400 animate-pulse" />
        <span className="text-xs font-medium text-blue-400">Running</span>
        {agentId ? (
          <code className="rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">
            {agentId.slice(0, 16)}...
          </code>
        ) : null}
      </>
    )
  }
  if (status === 'complete') {
    return (
      <>
        <span className="inline-block h-2 w-2 rounded-full bg-green-400" />
        <span className="text-xs font-medium text-green-400">Complete</span>
      </>
    )
  }
  if (status === 'unknown') {
    return (
      <span className="text-xs font-medium text-muted-foreground">Status unknown</span>
    )
  }
  return (
    <>
      <span className="inline-block h-2 w-2 rounded-full bg-red-400" />
      <span className="text-xs font-medium capitalize text-red-400">{status}</span>
    </>
  )
}

export type AgentDispatchPanelProps = {
  meta: AgentDispatchMeta
  /** When status is healed or updated, refresh inbox list + thread messages */
  onStatusUpdated?: () => void
}

/** Cloud agent dispatch status; live stream only when user opts in while job is active */
export function AgentDispatchPanel({ meta, onStatusUpdated }: AgentDispatchPanelProps) {
  const [status, setStatus] = useState<DispatchStatus>(() =>
    normalizeStatus(meta.dispatch_status)
  )
  const [prUrl, setPrUrl] = useState(meta.pr_url)
  const [branch, setBranch] = useState(meta.branch)
  const [watchLive, setWatchLive] = useState(false)

  const orgId = meta.dispatch_organization_id
  const agentId = meta.agentId
  const isActive = isActiveCloudDispatch({
    dispatchStatus: status === 'checking' ? meta.dispatch_status : status,
    isActive: status === 'running' || status === 'checking' ? undefined : false,
  })

  useEffect(() => {
    setStatus(normalizeStatus(meta.dispatch_status))
    setPrUrl(meta.pr_url)
    setBranch(meta.branch)
    setWatchLive(false)
  }, [meta.agentId, meta.dispatch_status, meta.pr_url, meta.branch])

  useEffect(() => {
    if (!agentId) return
    const resolvedAgentId = agentId
    let cancelled = false
    const priorStatus = meta.dispatch_status

    async function refresh() {
      setStatus('checking')
      const live = await fetchCloudAgentStatus(resolvedAgentId, orgId)
      if (cancelled) return
      if (!live) {
        setStatus(normalizeStatus(priorStatus) || 'unknown')
        return
      }
      const next = normalizeStatus(live.dispatchStatus)
      setStatus(next)
      if (live.prUrl) setPrUrl(live.prUrl)
      if (live.branch) setBranch(live.branch)

      const wasActive = isActiveCloudDispatch({ dispatchStatus: priorStatus })
      if (wasActive && !live.isActive) {
        onStatusUpdated?.()
      }
    }

    void refresh()
    return () => {
      cancelled = true
    }
  }, [agentId, orgId, meta.dispatch_status, onStatusUpdated])

  const showStream = Boolean(watchLive && agentId && isActive)

  return (
    <div className="flex flex-col gap-3 p-4" data-testid={testIds.inbox.agentDispatchPanel}>
      <div className="flex flex-wrap items-center gap-2">
        <StatusBadge status={status} agentId={agentId} />
      </div>

      {isActive && agentId && !watchLive ? (
        <div className="flex flex-col gap-1.5">
          <p className="text-xs text-muted-foreground">
            Watching status reconnects to the live feed; it does not start a new agent run.
          </p>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-8 w-fit gap-1.5"
            onClick={() => setWatchLive(true)}
            data-testid={testIds.inbox.agentWatchLive}
          >
            <Radio className="h-3.5 w-3.5" />
            Watch live
          </Button>
        </div>
      ) : null}

      {showStream && agentId ? (
        <AgentLiveStreamPanel
          agentId={agentId}
          organizationId={orgId}
          streamUrl={meta.stream_url}
          connectingHint="Reconnecting to status feed..."
        />
      ) : null}

      {status === 'complete' && prUrl ? (
        <div className="flex items-center justify-between gap-3 rounded-md border border-green-500/20 bg-green-500/5 p-3">
          <div className="min-w-0">
            <p className="text-xs font-medium text-foreground">PR ready to merge</p>
            {branch ? (
              <code className="text-xs text-muted-foreground">{branch}</code>
            ) : null}
          </div>
          <a
            href={prUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex shrink-0 items-center gap-1.5 text-xs text-green-400 underline hover:text-green-300"
            data-testid={testIds.inbox.agentPrLink}
          >
            <Link2 className="h-3 w-3" /> View PR
          </a>
        </div>
      ) : null}

      {(status === 'failed' || status === 'timeout' || status === 'error') && (
        <div className="rounded-md border border-red-500/20 bg-red-500/5 p-3">
          <p className="text-xs text-red-400">
            {status === 'timeout'
              ? 'Agent timed out (30 min limit).'
              : 'Agent run failed.'}{' '}
            Check the handoff doc and re-dispatch if needed.
          </p>
        </div>
      )}
    </div>
  )
}
